import { ExternalLink, ShieldCheck } from "lucide-react";

// Augusta Precious Metals — highest-commission affiliate partner. Featured
// prominently across the wealth site (homepage hero, sticky sidebar, library)
// per the user's directive. Compliance: every instance carries an
// "Educational only" disclaimer and a compensation disclosure.
const AUGUSTA_URL =
    process.env.REACT_APP_AUGUSTA_URL ||
    "https://learn.augustapreciousmetals.com/company-checklist-1/?apmtrkr_cid=1696&aff_id=1073&apmtrkr_cph=844-628-4303";

// Full-width hero-style block used on the homepage and the toolkit library.
export const AugustaFeatureBlock = ({ variant = "full" }) => (
    <section
        className="relative overflow-hidden rounded-2xl border border-amber-800/40 shadow-xl shadow-amber-900/10 my-8"
        style={{
            background:
                "linear-gradient(135deg,#92400e 0%,#b45309 55%,#d97706 100%)",
        }}
        data-testid="augusta-feature-block"
    >
        <div className="absolute right-0 top-0 h-full w-3 bg-amber-300" />
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center p-7 sm:p-9">
            <div className="text-white">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-900/40 text-amber-200 text-[10px] uppercase tracking-[0.18em] font-bold">
                    <ShieldCheck className="w-3 h-3" /> Partner Resource · Gold and Silver IRA
                </div>
                <h3 className="mt-3 font-serif text-2xl sm:text-3xl font-semibold leading-tight">
                    Free Gold &amp; Silver IRA Kit from Augusta Precious Metals
                </h3>
                <p className="mt-3 text-amber-100 text-sm sm:text-base leading-relaxed max-w-2xl">
                    A diversified retirement portfolio often includes physical precious metals.
                    Augusta will mail you a free educational kit plus a 1-on-1 web conference
                    with their Harvard-trained on-staff economist. No obligation.
                </p>
                <p className="mt-2 text-[11px] text-amber-200">
                    Educational only · We may receive compensation when you open an account.
                </p>
            </div>
            <div className="flex md:flex-col gap-3 md:items-end">
                <a
                    href={AUGUSTA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-amber-300 text-amber-950 font-bold text-sm tracking-wide hover:bg-amber-200 transition-colors whitespace-nowrap shadow-lg"
                    data-testid="augusta-feature-cta"
                >
                    Get Augusta's free kit <ExternalLink className="w-4 h-4" />
                </a>
            </div>
        </div>
    </section>
);

// Compact sidebar/sticky widget displayed on every wealth page (right rail).
export const AugustaSidebarCard = () => (
    <a
        href={AUGUSTA_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-xl border border-amber-800/30 shadow-lg shadow-amber-900/10 overflow-hidden hover:shadow-xl transition-shadow"
        style={{
            background:
                "linear-gradient(160deg,#92400e 0%,#b45309 100%)",
        }}
        data-testid="augusta-sidebar-card"
    >
        <div className="p-5 text-white">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-300">
                Partner Resource
            </div>
            <div className="mt-2 font-serif text-lg font-semibold leading-snug">
                Free Gold &amp; Silver IRA Kit
            </div>
            <p className="mt-2 text-[12px] text-amber-100 leading-snug">
                Augusta sends a free educational kit and a 1-on-1 web conference. No obligation.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-300 text-amber-950 font-bold text-[12px]">
                Get the free kit <ExternalLink className="w-3 h-3" />
            </div>
            <div className="mt-3 text-[9px] text-amber-200/80 leading-snug">
                Educational only · We may receive compensation.
            </div>
        </div>
    </a>
);

// Inline mini-banner used in dense layouts (e.g. inside other cards).
export const AugustaInlineBanner = () => (
    <a
        href={AUGUSTA_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 rounded-lg border border-amber-800/30 bg-gradient-to-r from-amber-900 to-amber-700 text-white hover:from-amber-800 hover:to-amber-600 transition-colors group"
        data-testid="augusta-inline-banner"
    >
        <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-300">
                Partner · Free Gold &amp; Silver IRA Kit
            </div>
            <div className="text-sm font-bold mt-0.5">
                Augusta Precious Metals — free educational kit
            </div>
        </div>
        <ExternalLink className="w-4 h-4 text-amber-200 group-hover:text-amber-100" />
    </a>
);

export { AUGUSTA_URL };
